import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { Link } from 'react-router-dom';
import { LanguageContext } from '../context/LanguageContext';
import '../styles/Navbar.css';
import logo from "../assets/logo_long.png";
import toggle from '../assets/icons/toggleMenu-Icon.svg';

const Navbar = () => {
  const [showNavbar, setShowNavbar] = useState(true); // State to Toggle the visibility of the navbar
  const [lastScrollY, setLastScrollY] = useState(0); // State to track the last scroll position
  const [scrollTimeout, setScrollTimeout] = useState(null); // State to track the timeout for scroll event (how long to wait before hiding the navbar)
  const [menuOpen, setMenuOpen] = useState(false); // State to track if the menu (sandwitch button) is open
  const navbarLinksRef = useRef(null); // Ref to store navbar-link elements
  const languageMenuRef = useRef(null); // Ref to store language menu element
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false); // State to track if the language menu is open
  const {language, changeLanguage, translations, isLoading} = useContext(LanguageContext); // Ref to store the current language
  const supportedLanguages = {
    en: '🇬🇧 English',
    bg: '🇧🇬 Български',
    de: '🇩🇪 Deutsch',
    fr: '🇫🇷 Français',
    it: '🇮🇹 Italiano',
    es: '🇪🇸 Español',
    ru: '🇷🇺 Русский',
    zh: '🇨🇳 中文',
    ja: '🇯🇵 日本語',
    ko: '🇰🇷 한국어',
    pt: '🇵🇹 Português',
    ne: '🇳🇱 Nederlands',
  }

  const handleScroll = useCallback(() => {
    if (scrollTimeout) {
      clearTimeout(scrollTimeout);
    }

    setMenuOpen(false);
    setLanguageMenuOpen(false);

    // Toggle the navbar visibility based on scroll direction
    if (window.scrollY > lastScrollY) {
      const timeout = setTimeout(() => {
        setShowNavbar(false);
      }, 10);
      setScrollTimeout(timeout);
    } else {
      setShowNavbar(true);
    }

    setLastScrollY(window.scrollY);
  }, [lastScrollY, scrollTimeout]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, [handleScroll, scrollTimeout]);

  const toggleMenu = () => {
    if (menuOpen) {
      navbarLinksRef.current.classList.add('closing');

      setTimeout(() => {
        setMenuOpen(false);
        navbarLinksRef.current.classList.remove('closing');
      }, 300);
    } else {
      setMenuOpen(true);
    }
  };

  const closeMenu = () => {
    setMenuOpen(false);
  };

  const toggleLanguageMenu = () => {
    setLanguageMenuOpen(!languageMenuOpen);
  };

  // Close language menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (languageMenuRef.current && !languageMenuRef.current.contains(event.target)) {
        setLanguageMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLanguageChange = (lang) => {
    changeLanguage(lang);
    setLanguageMenuOpen(false);
  };

  return (
    <nav className={`navbar ${showNavbar ? 'navbar-show' : 'navbar-hide'}`}>
      <div className="navbar-container">
        <div className="navbar-logo">
          <Link to="/" onClick={closeMenu}>
            <img src={logo} alt="Logo" />
          </Link>
        </div>
        <div className="menu-icon" onClick={toggleMenu}>
          <img src={toggle} alt="toggleMenu" />
        </div>
        <div ref={navbarLinksRef} className={`navbar-links ${menuOpen ? 'active' : ''}`}>
          <Link to="/" onClick={closeMenu}>{translations.navbar.home}</Link>
          <a href={require("../assets/pdf/Viktor Asenov CV - 31.07.2025.pdf")} target='_blank' rel="noreferrer" onClick={closeMenu}>{translations.navbar.cv}</a>
          <Link to="/about" onClick={closeMenu}>{translations.navbar.aboutMe}</Link>
          <Link to="/contact" onClick={closeMenu}>{translations.navbar.contact}</Link>
          <Link to="/services-apps" onClick={closeMenu}>{translations.navbar.servicesApps}</Link>

          <div className="language-switcher" ref={languageMenuRef}>
            <button className="language-button" onClick={toggleLanguageMenu}>
              {supportedLanguages[language]}
              <span className={`arrow ${languageMenuOpen ? 'up' : 'down'}`}>▼</span>
            </button>
            {languageMenuOpen && (
              <div className="language-dropdown">
                {Object.entries(supportedLanguages).map(([code, name]) => (
                  <button
                    key={code}
                    className={`language-option ${code === language ? 'active' : ''}`}
                    onClick={() => handleLanguageChange(code)}
                    disabled={isLoading}
                  >
                    {name}
                  </button>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>
    </nav>
  );
};

export default Navbar;
